import { Injectable } from '@angular/core';

/**
 * Tracks the customerPlayback setting for the webapp. localStorage is the source of truth so the
 * setting survives navigation without being threaded through route params. A value present in the
 * URL takes precedence and overwrites the stored value.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPlaybackService {
  private static readonly STORAGE_KEY = 'customerPlayback';

  /**
   * If the URL carries a customerPlayback value it wins and is persisted; otherwise the stored
   * value is returned. Call this wherever the URL param may appear (e.g. on entry).
   */
  resolveFromUrl(urlValue: string | null): string | null {
    if (urlValue !== null) {
      this.set(urlValue);
      return urlValue;
    }
    return this.get();
  }

  get(): string | null {
    try {
      return localStorage.getItem(CustomerPlaybackService.STORAGE_KEY);
    } catch {
      // localStorage can throw in Safari private mode; treat as unset.
      return null;
    }
  }

  isCustomerPlaybackEnabled(): boolean {
    const currentStorageVal = this.get();
    return currentStorageVal ? currentStorageVal.toLowerCase() === 'true' : false;
  }

  private set(value: string): void {
    try {
      localStorage.setItem(CustomerPlaybackService.STORAGE_KEY, value);
    } catch {
      // localStorage unavailable (e.g. Safari private mode); skip persistence.
    }
  }
}
